import { useState } from 'react';

import type { CreateTenantAdminRequest } from '../api';
import { useLicensePlans } from '../hooks/useLicensePlans';

interface CreateTenantModalProps {
  open: boolean;
  busy: boolean;
  onClose: () => void;
  onSubmit: (request: CreateTenantAdminRequest) => void;
}

const EMPTY = {
  tenantId: '',
  name: '',
  licenseType: '',
  licenseExpiresAt: '',
  apiKeyPlaintext: '',
};

export function CreateTenantModal({ open, busy, onClose, onSubmit }: CreateTenantModalProps): JSX.Element | null {
  const { data: plans } = useLicensePlans();
  const [form, setForm] = useState({ ...EMPTY });

  if (!open) return null;

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    onSubmit({
      ...form,
      licenseExpiresAt: new Date(form.licenseExpiresAt).toISOString(),
    });
  };

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-tenant-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-2xl border border-slate-200/70 bg-white p-4 shadow-xl sm:p-6"
      >
        <h2 id="create-tenant-title" className="text-lg font-semibold text-slate-900">Crear tenant</h2>

        <div className="mt-5 space-y-4">
          <div>
            <label className="label" htmlFor="ct-id">Tenant ID</label>
            <input id="ct-id" className="input font-mono" placeholder="tenant_xxxxxx" value={form.tenantId} onChange={set('tenantId')} disabled={busy} required />
          </div>
          <div>
            <label className="label" htmlFor="ct-name">Nombre</label>
            <input id="ct-name" className="input" value={form.name} onChange={set('name')} disabled={busy} required />
          </div>
          <div>
            <label className="label" htmlFor="ct-license">Plan</label>
            <select id="ct-license" className="input" value={form.licenseType} onChange={set('licenseType')} disabled={busy} required>
              <option value="">Seleccionar plan…</option>
              {(plans ?? []).map((p) => (
                <option key={p.licenseType} value={p.licenseType}>{p.displayName}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="ct-expires">Expiración de licencia</label>
            <input id="ct-expires" type="date" className="input" value={form.licenseExpiresAt} onChange={set('licenseExpiresAt')} disabled={busy} required />
          </div>
          <div>
            <label className="label" htmlFor="ct-key">API key inicial</label>
            <input id="ct-key" type="password" autoComplete="new-password" className="input" value={form.apiKeyPlaintext} onChange={set('apiKeyPlaintext')} disabled={busy} required />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={busy}>{busy ? 'Creando…' : 'Crear tenant'}</button>
        </div>
      </form>
    </div>
  );
}
